/* eslint-disable */ 
import router from '@/router'
import jwtDecode from 'jwt-decode'
import logIn from '@/graphql/Login.gql'
import getActiveSessionsQuery from '@/graphql/Sessions/GetActiveSessions.gql'
import revokeSessionMutation from '@/graphql/Sessions/RevokeSession.gql'
import resetPassword from '@/graphql/ResetPassword.gql'
import forgetPassword from '@/graphql/ForgetPassword.gql'
import changePassword from '@/graphql/ChangePassword.gql'
import validateSessionQuery from '@/graphql/ValidateSession.gql'
import profileMeQuery from '@/graphql/ProfileMe.gql'
import createFinancialCompany from '@/graphql/createFinancialCompany.gql'
import getTokenConfirm from '@/graphql/getTokenConfirm.gql'
import confirmAccount from '@/graphql/confirmAccount.gql'
import resendOtp from '@/graphql/resendOtp.gql'
import { initAmplitude, trackUserLogin } from '@/utils/amplitude'
import { onLogin, onLogout, createProvider } from '../../vue-apollo'
import inactivityManager from '@/utils/inactivityManager'
import Logout from '@/graphql/Logout.gql'

const apollo = createProvider()

export default {
  async signin ({ commit, dispatch }, { email, password, ip, forceLogin, userAgent }) {
    try {
      const result = await apollo.defaultClient.query({
        query: logIn,
        fetchPolicy: 'no-cache',
        variables: {
          email,
          password,
          ip,
          forceLogin,
          userAgent
        }
      })
      
      const payload = jwtDecode(result.data.logIn)
      initAmplitude()

      // Trackeas login con propiedades del usuario
      trackUserLogin({
        userId: payload.userId,
        email: payload.email,
        financial: payload.identifier
      })
      await onLogin(apollo.defaultClient, result.data.logIn)
      commit('loginSuccess', { ...payload })

      router.replace({ name: 'home' })
    } catch (error) {
      const gqlError = error.graphQLErrors[0]
      const code = gqlError?.code || gqlError.extensions?.code
      return {
        error: true,
        message: gqlError.message,
        code
      }
    }
  },

  async logout ({ commit }) {
    const token = localStorage.getItem('access-token')
    if (token) {
      try {
        await apollo.defaultClient.query({
          query: Logout,
          variables: { token },
          fetchPolicy: 'no-cache'
        })
      } catch (error) {
        console.error('Error al cerrar sesión:', error)
      }
    }
    await onLogout(apollo.defaultClient)
    router.replace({ name: 'login' })
  },

  async validateSession ({ dispatch }) {
    try {
      await apollo.defaultClient.query({
        query: validateSessionQuery,
        fetchPolicy: 'no-cache'
      })
      return true
    } catch (error) {
      dispatch('showError', error)
    }
  },

  async profileMe ({ dispatch, commit }) {
    try {
      const result = await apollo.defaultClient.query({
        query: profileMeQuery,
        fetchPolicy: 'no-cache'
      })
      commit('addUserProfile', result.data.me)

      const minutes = result.data.me?.role?.configuration?.inactivityTimeMinutes
      if (localStorage.getItem('access-token')) {
        if (minutes === null || minutes === 0) {
          inactivityManager.destroy()
          window.__inactivityDismissWarning && window.__inactivityDismissWarning()
        } else {
          const resolvedMinutes = minutes === undefined ? 20 : minutes
          const expectedMs = resolvedMinutes * 60 * 1000
          if (!inactivityManager._active || inactivityManager._inactivityMs !== expectedMs) {
            inactivityManager.init({
              inactivityMinutes: minutes,
              onShowWarning: () => { window.__inactivityShowWarning && window.__inactivityShowWarning() },
              onDismissWarning: () => { window.__inactivityDismissWarning && window.__inactivityDismissWarning() },
              onLogout: () => { window.__inactivityLogout && window.__inactivityLogout() }
            })
          }
        }
      }

      if (result && (!result?.data?.me?.passwordConfirm || result?.data?.me?.changePassword)) {
        router.push({ name: 'update-password' })
      }
      return result.data.me
    } catch (error) {
      dispatch('logout')
      dispatch('showError', error)
    }
  },

  async changePass ({ dispatch }, { oldPassword, newPassword }) {
    try {
      const result = await apollo.defaultClient.mutate({
        mutation: changePassword,
        variables: {
          oldPassword: oldPassword,
          newPassword: newPassword
        }
      })
      dispatch('addTemporalNotification', {
        type: 'success',
        message: '¡Contraseña actualizada con éxito!'
      })
      return result
    } catch (error) {
      dispatch('showError', error)
    }
  },

  async resetPass ({ dispatch }, { token, password }) {
    try {
      const result = await apollo.defaultClient.mutate({
        mutation: resetPassword,
        variables: {
          token: token,
          password: password
        }
      })
      dispatch('addTemporalNotification', {
        type: 'success',
        message: 'La contraseña se ha restablecido correctamente'
      })
      return result
    } catch (error) {
      dispatch('showError', error)
    }
  },

  async forgetPass ({ dispatch }, { email }) {
    try {
      const result = await apollo.defaultClient.mutate({
        mutation: forgetPassword,
        variables: {
          email: email
        }
      })
      dispatch('addTemporalNotification', {
        type: 'success',
        message:
          'Se ha enviado exitosamente el correo con las instrucciones para el cambio de contraseña'
      })
      return result
    } catch (error) {
      dispatch('showError', error)
    }
  },
  async createFinancialCompany ({ dispatch }, body) {
    try {
      const { financialCompany, user } = body
      const { data } = await apollo.defaultClient.mutate({
        mutation: createFinancialCompany,
        fetchPolicy: 'no-cache',
        variables: {
          financialCompany,
          user
        }
      })
      dispatch('addTemporalNotification', {
        type: 'success',
        message: '¡Empresa financiera creada exitosamente!'
      })
      return data.signUp
    } catch (error) {
      return { isError: true, message: error.graphQLErrors[0].message }
    }
  },
  async getTokenConfirm ({ commit, dispatch }, { token }) {
    try {
      const { data } = await apollo.defaultClient.query({
        query: getTokenConfirm,
        variables: {
          token
        }
      })
      return data.getTokenConfirm
    } catch (error) {
      return { error: true, message: error.graphQLErrors[0].message, code: error.graphQLErrors[0].code }
    }
  },
  async confirmAccount ({ dispatch }, body) {
    try {
      const { otp, token } = body
      const { data } = await apollo.defaultClient.mutate({
        mutation: confirmAccount,
        fetchPolicy: 'no-cache',
        variables: {
          otp,
          token
        }
      })
      return data.confirmAccount
    } catch (error) {
      return { isError: true, message: error.graphQLErrors[0].message }
    }
  },
  async resendOtp ({ commit, dispatch }, { email }) {
    try {
      const { data } = await apollo.defaultClient.query({
        query: resendOtp,
        variables: {
          email
        }
      })
      return data.resendOtp
    } catch (error) {
      return { error: true, message: error.graphQLErrors[0].message, code: error.graphQLErrors[0].code }
    }
  },

  async getActiveSessions ({ dispatch }) {
    try {
      const { data } = await apollo.defaultClient.query({
        query: getActiveSessionsQuery,
        fetchPolicy: 'no-cache'
      })
      return data.getActiveSessions
    } catch (error) {
      dispatch('showError', error)
      return []
    }
  },

  async revokeSession ({ dispatch }, { sessionId }) {
    try {
      const { data } = await apollo.defaultClient.mutate({
        mutation: revokeSessionMutation,
        variables: { sessionId }
      })
      return data.revokeSession
    } catch (error) {
      dispatch('showError', error)
      return false
    }
  }
}
